import { AdminCategoriesTypeFilter, type AdminCategorySelectListDto } from '@@types/api.types';
import { ClearableSearchInput } from '@components/ui/clearable-search-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@components/ui/table';
import { type ColumnDef, type SortingState, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ArrowDownWideNarrowIcon, ArrowUpDown, ArrowUpNarrowWideIcon, Loader2 } from 'lucide-react';
import React, { useMemo } from 'react';
import { createCategoriesColumns } from '../admin-categories-columns.config';

type AdminCategoriesTableProps = {
  categories: AdminCategorySelectListDto[];
  categoryType: AdminCategoriesTypeFilter;
  isLoading: boolean;
  onRowClick: (categoryId: string) => void;
  onSearchChange: (search: string) => void;
  onSortingChange: (sorting: SortingState) => void;
  onTypeChange: (type: AdminCategoriesTypeFilter) => void;
  search: string;
  sorting: SortingState;
};

export const AdminCategoriesTable: React.FC<AdminCategoriesTableProps> = (props) => {
  const {
    categories,
    categoryType,
    isLoading,
    onRowClick,
    onSearchChange,
    onSortingChange,
    onTypeChange,
    search,
    sorting,
  } = props;

  const columns = useMemo<ColumnDef<AdminCategorySelectListDto>[]>(() => createCategoriesColumns(), []);

  const table = useReactTable({
    columns,
    data: categories,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    onSortingChange,
    state: {
      sorting,
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <Select
          value={categoryType}
          onValueChange={(value) => onTypeChange(value as AdminCategoriesTypeFilter)}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={AdminCategoriesTypeFilter.ALL}>All Categories</SelectItem>
            <SelectItem value={AdminCategoriesTypeFilter.MAIN}>Parent Categories</SelectItem>
            <SelectItem value={AdminCategoriesTypeFilter.SUB}>Subcategories</SelectItem>
          </SelectContent>
        </Select>
        <ClearableSearchInput
          onChange={(value) => onSearchChange(value ?? '')}
          placeholder="Search by name..."
          value={search}
        />
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center h-48 rounded-xl border">
          <Loader2 className="size-6 animate-spin text-gray-500" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    if (header.isPlaceholder) {
                      return <TableHead key={header.id} />;
                    }

                    const canSort = header.column.getCanSort();
                    const sorted = header.column.getIsSorted();

                    return (
                      <TableHead
                        key={header.id}
                        className="text-xs font-semibold text-gray-500"
                        style={{ width: header.getSize() }}
                      >
                        {canSort ? (
                          <button
                            className="flex items-center gap-1 text-xs font-semibold text-gray-600"
                            onClick={header.column.getToggleSortingHandler()}
                            type="button"
                            aria-label={`Sort by ${typeof header.column.columnDef.header === 'string' ? header.column.columnDef.header : 'column'}`}
                            aria-sort={sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : 'none'}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {sorted === 'asc' && <ArrowUpNarrowWideIcon className="size-3.5" />}
                            {sorted === 'desc' && <ArrowDownWideNarrowIcon className="size-3.5" />}
                            {!sorted && <ArrowUpDown className="size-3.5 text-gray-400" />}
                          </button>
                        ) : (
                          flexRender(header.column.columnDef.header, header.getContext())
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => onRowClick(row.original.id)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        style={{ width: cell.column.getSize() }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center text-sm text-gray-500"
                  >
                    No categories found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
